/*
 * Changes:
 * - Added 'use client' directive for Next.js 13+ compatibility
 * - File contains client-side features: Chakra UI provider and theme configuration
 * - Updated: 2025-01-27 - Added primaryColor prop to allow dynamic primary color override
 *   This enables app-specific theming (e.g., console vs trade) without creating separate color schemes
 */

'use client'

import React from 'react'
import {ChakraProvider, createMultiStyleConfigHelpers, extendTheme} from '@chakra-ui/react'
import {tableAnatomy as parts} from '@chakra-ui/anatomy'
import {StorageManager} from '@chakra-ui/react/dist/types/color-mode/storage-manager'

const {definePartsStyle} = createMultiStyleConfigHelpers(parts.keys)

const colors = {
  neutral: {
    100: '#FFFFFF',
    200: '#F6F8FA',
    500: '#F6F8FA'
  },
  neutral3: '#E4E7EE',
  neutral4: '#898997',
  primary: {
    50: '#eef0ff',
    100: '#dfe1ff',
    200: '#bdc2ff',
    300: '#9ba3ff',
    400: '#7984ff',
    500: '#424AF9',
    600: '#3338C8',
    700: '#282b9e',
    800: '#1d1f74',
    900: '#12134a'
  },
  // TODO Adnan: Remove this once we've updated the design system
  primary2: {
    50: '#eef0ff',
    100: '#dfe1ff',
    200: '#bdc2ff',
    300: '#9ba3ff',
    400: '#7984ff',
    500: '#424AF9',
    600: '#3338C8',
    700: '#282b9e',
    800: '#1d1f74',
    900: '#12134a'
  },

  neutral2: {
    500: '#F6F8FA'
  },
  muted: {
    DEFAULT: '#f3f4f6',
    foreground: '#6b7280'
  },
  accent: {
    DEFAULT: '#fff',
    foreground: '#000'
  }
}

const fonts = {
  body: 'Open sans, sans-serif',
  heading: 'Inter, sans-serif',
  mono: 'Menlo, monospace'
}

const fontSizes = {
  headline1: '42px',
  headline2: '28px',
  headline3: '24px',
  headline4: '12px',
  bodyLarge: '24px',
  bodyMedium: '18px',
  bodySmall: '14px',
  bodyXSmall: '12px'
}

// TODO Adnan: Figure out if there's a better way than & to override the default styles
const components = {
  Stat: {
    baseStyle: {
      label: {
        color: 'neutral4',
        py: 2,
        '&': {
          fontSize: 'bodySmall'
        }
      },
      number: {
        '&': {
          fontSize: 'headline2'
        }
      },
      helpText: {
        color: 'neutral4',
        '&': {
          fontSize: 'bodySmall'
        }
      }
    }
  },
  Tabs: {
    variants: {
      boxed: {
        tablist: {
          bg: 'muted.DEFAULT',
          p: '4px',
          borderRadius: 'lg',
          display: 'inline-flex',
          gap: '4px'
        },
        tab: {
          bg: 'transparent',
          color: 'muted.foreground',
          fontSize: 'bodySmall',
          fontWeight: 'medium',
          px: '4',
          py: '2',
          borderRadius: 'md',
          _selected: {
            bg: 'accent.DEFAULT',
            color: 'primary2.500',
            boxShadow: 'sm',
            fontWeight: 'semibold'
          },
          _hover: {
            bg: 'rgba(255, 255, 255, 0.5)',
            color: 'primary2.500'
          }
        },
        tabpanel: {
          pt: '4',
          px: '0'
        }
      },
      'boxed-sm': {
        tablist: {
          bg: 'muted.DEFAULT',
          p: '3px',
          borderRadius: 'md',
          display: 'inline-flex',
          gap: '2px',
          boxShadow: 'inset 0 1px 2px 0 rgb(0 0 0 / 0.05)'
        },
        tab: {
          bg: 'transparent',
          color: 'muted.foreground',
          fontSize: 'xs',
          fontWeight: 'medium',
          px: '3',
          py: '1',
          borderRadius: 'md',
          _selected: {
            bg: 'accent.DEFAULT',
            color: 'primary2.500',
            boxShadow: 'sm',
            fontWeight: 'semibold'
          },
          _hover: {
            bg: 'rgba(255, 255, 255, 0.5)',
            color: 'primary2.500'
          }
        },
        tabpanel: {
          pt: '3',
          px: '0'
        }
      }
    }
  },
  Table: {
    baseStyle: definePartsStyle({
      th: {
        textTransform: 'none',
        fontWeight: 'normal',
        letterSpacing: 'normal',
        '&': {
          color: 'neutral4',
          fontSize: 'bodyXSmall'
        }
      }
    })
  },
  Badge: {
    baseStyle: {
      borderRadius: '2rem',
      padding: '0.3rem 1rem'
    }
  },
  Card: {
    baseStyle: {
      borderRadius: '1rem'
    }
  },
  Modal: {
    baseStyle: {
      overlay: {
        bg: `rgba(${hexToRgb(colors.primary2[500])}, 0.8)` // 50% opacity
      }
    }
  },
  Stepper: {
    baseStyle: {
      step: {
        display: 'block',
        margin: '1rem'
      },
      alignItems: 'default',
      stepper: {
        '&[data-orientation=horizontal]': {
          alignItems: 'baseline',
          '@media screen and (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'stretch'
          }
        }
      },
      separator: {
        margin: 0
      }
    }
  },
  Input: {
    variants: {
      outline: {
        field: {
          borderRadius: 'md',
          _focus: {
            boxShadow: 'outline'
          }
        }
      }
    },
    sizes: {
      xs: {
        field: {
          borderRadius: 'sm'
        }
      },
      sm: {
        field: {
          borderRadius: 'lg'
        }
      }
    }
  },
  Select: {
    variants: {
      outline: {
        field: {
          borderRadius: 'md',
          _focus: {
            boxShadow: 'outline'
          }
        }
      }
    },
    sizes: {
      xs: {
        field: {
          borderRadius: 'sm'
        }
      },
      sm: {
        field: {
          borderRadius: 'md'
        }
      }
    }
  },
  Button: {
    variants: {
      outline: {
        color: 'primary2.500',
        borderColor: 'primary2.500',
        _hover: {
          bg: 'primary2.500',
          color: 'white'
        }
      },
      ghost: {
        color: 'primary2.500',
        _hover: {
          bg: 'primary2.500',
          color: 'white'
        }
      }
    }
  }
}

// Helper function to generate color scale from a base color
// Generates Chakra UI color scale (50-900) where 500 is the base color
function generateColorScale(baseColor: string): {50: string; 100: string; 200: string; 300: string; 400: string; 500: string; 600: string; 700: string; 800: string; 900: string} {
  // Parse hex color to RGB
  const hex = baseColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Generate lighter shades (50-400) by mixing with white
  // Generate darker shades (600-900) by reducing brightness
  const lighten = (amount: number) => {
    const factor = amount / 100
    return {
      r: Math.round(r + (255 - r) * factor),
      g: Math.round(g + (255 - g) * factor),
      b: Math.round(b + (255 - b) * factor)
    }
  }

  const darken = (amount: number) => {
    const factor = amount / 100
    return {
      r: Math.round(r * (1 - factor)),
      g: Math.round(g * (1 - factor)),
      b: Math.round(b * (1 - factor))
    }
  }

  const toHex = (rgb: {r: number; g: number; b: number}) => {
    return `#${[rgb.r, rgb.g, rgb.b].map((x) => x.toString(16).padStart(2, '0')).join('')}`
  }

  return {
    50: toHex(lighten(90)),
    100: toHex(lighten(80)),
    200: toHex(lighten(60)),
    300: toHex(lighten(40)),
    400: toHex(lighten(20)),
    500: baseColor, // Base color
    600: toHex(darken(15)),
    700: toHex(darken(30)),
    800: toHex(darken(45)),
    900: toHex(darken(60))
  }
}

interface ChakraProviderWithThemeProps {
  children: React.ReactNode
  colorModeManager?: StorageManager
  primaryColor?: string // Optional primary color override for app-specific theming
}

export const ChakraProviderWithTheme = ({children, colorModeManager, primaryColor}: ChakraProviderWithThemeProps) => {
  // If primaryColor is provided, generate color scales for it
  // Use useMemo to avoid regenerating theme on every render
  const finalColors = React.useMemo(
    () =>
      primaryColor
        ? {
            ...colors,
            primary: generateColorScale(primaryColor),
            primary2: generateColorScale(primaryColor) // Also update primary2 to match
          }
        : colors,
    [primaryColor]
  )

  const theme = React.useMemo(() => extendTheme({colors: finalColors, fonts, fontSizes, components}), [finalColors])

  return (
    <ChakraProvider
      colorModeManager={colorModeManager}
      toastOptions={{
        defaultOptions: {
          duration: 3000,
          isClosable: true,
          position: 'bottom-right'
        }
      }}
      theme={theme}>
      {children}
    </ChakraProvider>
  )
}

// Function to convert hex color to rgb
function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null
}
